import { BaseModel } from './baseModel';

/**
 * Shape of the `json` field when used for guided tour steps (slot === 'walkthrough').
 * Maps to @reactour/tour: targetSelector → selector, placement → position.
 */
export interface AdditionalContentTourConfig {
  /** CSS selector for the element to highlight (e.g. [data-tour='welcome-banner']) */
  targetSelector: string;
  /** Tooltip position relative to target; maps to @reactour/tour position */
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'center';
}

export interface AdditionalContent extends BaseModel {
  // Required fields for identification and rendering
  // id comes from BaseModel - used as key for rendering
  page: string; // Required - specifies where component is used
  slot: string; // Required - specifies where component is used
  order: number; // Required - determines sequential display order
  contentType?: string; // Required for rendering - specifies how to render (image, video, json, html)
  
  // Optional fields - only needed based on contentType
  title?: string;
  description?: string;
  level?: string; // Optional - shown as badge on card
  instructor?: string;
  instructorAvatarUrl?: string;
  imageUrl?: string; // Required for contentType="image"
  videoUrl?: string; // Required for contentType="video"
  html?: string; // Required for contentType="html"
  /** Generic payload; for tour steps (slot 'walkthrough') use AdditionalContentTourConfig */
  json?: Record<string, unknown> | string;
}

/** Type guard: true when json is a valid tour step config (has targetSelector). */
export function isTourConfig(
  json: AdditionalContent['json']
): json is AdditionalContentTourConfig & Record<string, unknown> {
  return (
    typeof json === 'object' &&
    json !== null &&
    !Array.isArray(json) &&
    typeof (json as Record<string, unknown>).targetSelector === 'string'
  );
}

export interface AdditionalContentSearchRequest {
  page: string;
  slot: string;
}
